'use client';

import * as React from 'react';
import { languages } from '@/app/constants';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { hideModal } from '@/store/reducers/ui-slice';
import { Button } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useDispatch } from 'react-redux';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';
import { toast } from '@/components/core/toaster';

interface LanguageItems {
  partitionKey: string;
  rowKey: string;
  [key: string]: unknown;
}

export function CreateTranslationModalContent(): React.JSX.Element | null {
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = React.useState({ language: 'en' });
  const [dto, setDto] = React.useState<LanguageItems>({
    ...Object.entries(languages).reduce<Record<string, string>>((acc, [key]) => {
      acc[key] = '';
      return acc;
    }, {}),
    partitionKey: 'b2c',
    rowKey: '',
  });
  const handleClose = (): void => {
    dispatch(hideModal());
  };
  const dispatch = useDispatch();

  const fields: CustomConfigInputProps[] = [
    {
      name: 'rowKey',
      label: 'Row Key',
      required: true,
      type: InputType.INPUT,
    },
    {
      name: 'en',
      label: 'English',
      required: true,
      type: InputType.INPUT,
    },
    ...Object.entries(languages)
      .filter(([key]) => key !== 'en')
      .map(([key, val]) => ({
        label: val.label,
        type: InputType.INPUT,
        name: key,
        required: true,
        hidden: selectedLanguage.language !== key && key !== 'en',
      })),
  ];

  const handleSubmit = async (): Promise<void> => {
    setIsDisabled(true);

    try {
      const response = await fetch('/api/translation', {
        method: 'post',
        body: JSON.stringify(dto),
      });
      const result = await response.json();
      if (response.ok && result.success && result.data) {
        toast.success('Translation record created successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error('Error: 500 | Unable to create new translation record');
      }
    } catch (e) {
      toast.error('Error: 500 | Unable to create new translation record');
    } finally {
      setIsDisabled(false);
    }
  };
  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <CustomConfigInputV2
          label="Select Language"
          value={selectedLanguage.language}
          name="language"
          type={InputType.SELECT}
          onChange={(val) => {
            setSelectedLanguage({ language: val as string });
          }}
          menuItems={Object.entries(languages).map(([key, value]) => ({ label: value.label, value: key }))}
        />

        {fields.map((inputItem, index) => (
          <CustomConfigInputV2 key={index} {...inputItem} defaultData={dto} setDefaultData={setDto} />
        ))}
        <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
          <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
            Close
          </Button>

          <Button loading={isDisabled} color="primary" type="submit" sx={{ flex: '1 1 auto' }} variant="contained">
            Save Changes
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
